import * as React from "react";
import { Button, Card, CardBody, CardImg, Col, Form, FormGroup, Input, Label, Row } from "reactstrap";
import { AppContext } from "src/ui/state/Contextes";
import * as Messages from "../../foundation/Messages";
import { ISplashImage, PubImageOrientation } from "src/models/dto/TitlesRequest";
import { Loading } from "src/ui/foundation/Controls";

interface ICurrentSplashImagesProps {
  titleId: number;
}

export const CurrentSplashImages: React.FC<ICurrentSplashImagesProps> = ({ titleId }) => {
  const [splashImages, setSplashImages] = React.useState<ISplashImage[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const appContext = React.useContext(AppContext);
  const [splashImage, setSplashImage] = React.useState<string>("");
  const [splashImageWidth, setSplashImageWidth] = React.useState<number>(0);
  const [splashImageHeight, setSplashImageHeight] = React.useState<number>(0);

  const fetchSplashImages = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await appContext.getSplashPages({
        TitleId: titleId,
      });

      if (response.valid()) {
        setSplashImages(response.data.SplashPages as ISplashImage[]);
      } else {
        if (response.errors.length > 0) {
          Messages.Notify.error("Failed to load splash images: " + response.errors[0].Message);
        } else {
          Messages.Notify.error("An error occurred while loading splash images");
        }
      }
    } catch (error) {
      Messages.Notify.error("Failed to fetch splash images");
    } finally {
      setLoading(false);
    }
  }, [titleId]);

  const handleDeleteSplashImage = async (splashId: number) => {
    try {
      const response = await appContext.deleteSplashPage({
        TitleId: titleId,
        SplashId: splashId,
      });

      if (response.valid()) {
        Messages.Notify.success("Splash image deleted successfully");
        fetchSplashImages();
      } else {
        if (response.errors.length > 0) {
          Messages.Notify.error("Delete failed: " + response.errors[0].Message);
        } else {
          Messages.Notify.error("An error occurred while deleting the splash image");
        }
      }
    } catch (error) {
      Messages.Notify.error("Failed to delete splash image");
    }
  };

  const getFriendlyOrientation = (orientation: number) => {
    switch (orientation) {
      case PubImageOrientation.Undefined:
        return "Undefined";
      case PubImageOrientation.Portrait:
        return "Portrait";
      case PubImageOrientation.Landscape:
        return "Landscape";
      default:
        return "Unknown";
    }
  };

  React.useEffect(() => {
    fetchSplashImages();
  }, [fetchSplashImages]);

  if (loading) {
    return <div>Loading splash images...</div>;
  }

  const addSpashPage = async () => {
    let res = await appContext.addSplashPage({
      TitleId: titleId,
      SplashData: splashImage,
      SplashWidth: splashImageWidth,
      SplashHeight: splashImageHeight,
    });
    if (!res.valid()) {
      if (res.errors.length > 0) {
        Messages.Notify.error("Save failed. Server reported: " + res.errors[0].Message);
      } else {
        Messages.Notify.error("An error occured while executing the communication");
      }
    } else {
      Messages.Notify.success("Splash page added successfully.");
      // Clear the splash images and fetch them again.
      setSplashImages([]);
      setSplashImage("");
      setSplashImageWidth(0);
      setSplashImageHeight(0);
      fetchSplashImages();
    }
  };

  const handleSplashPageChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.persist();
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result as string;
        const fileData = base64String.split(",")[1]; // Extract the base64 data

        // Create an image element to get dimensions
        const img = new Image();
        img.src = base64String;
        img.onload = () => {
          setSplashImageWidth(img.width);
          setSplashImageHeight(img.height);
        };

        setSplashImage(fileData); // Set the base64 data without the prefix
      };
      reader.readAsDataURL(file);
    }
  };
  const downloadSplash = (splashId: number, imageData: string) => {
    const link = document.createElement("a");
    link.href = `data:image/png;base64,${imageData}`; // Use the base64 data directly
    link.download = "splash_" + splashId + ".png"; // Set a default filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Form style={{ paddingTop: "2rem" }}>
        <Row>
          <h5>Add new splash pages for the title. Spash pages must be in .png format and follow resolution formats of 16:9, 16:10, 3:2, or 4:3.</h5>
          <FormGroup check style={{ flex: "1" }}>
            <Label check for="splashImage">
              Splash Image (PNG only)
            </Label>
            <Input type="file" accept=".png" name="splashImage" id="splashImage" onChange={handleSplashPageChanged} />
          </FormGroup>
        </Row>
        <Row className="formButtons">
          <Button disabled={splashImage.length === 0} color="info" outline onClick={() => addSpashPage()}>
            Add New Splash Page
          </Button>
        </Row>
      </Form>
      <Loading isLoading={loading} className="splash-images-container" theme="opaque" status="Loading splash images...">
        <h3>Current Splash Images</h3>
        {splashImages.length === 0 ? (
          <p>No splash images available for this title.</p>
        ) : (
          <Row>
            {splashImages.map((splash, i) => (
              <Col key={i} md={2} className="mb-4">
                <Card>
                  {splash.ImageData && splash.ImageData.length > 0 && (
                    <CardImg style={{ maxWidth: "15rem", height: "auto" }} top src={`data:image/png;base64,${splash.ImageData as string}`} alt="Splash image" />
                  )}
                  {(!splash.ImageData || splash.ImageData.length === 0) && (
                    <p style={{ padding: "1rem" }}>No image has been set for this aspect ratio and orientation.</p>
                  )}
                  <CardBody>
                    <p>
                      Dimensions {splash.Width}:{splash.Height}
                    </p>
                    <p>Orientation: {getFriendlyOrientation(splash.Orientation)}</p>
                    <Row>
                      <Col className="formButtons">
                        <Button color="danger" outline onClick={() => handleDeleteSplashImage(splash.Id as number)}>
                          Delete
                        </Button>
                        {splash.ImageData && splash.ImageData.length > 0 && (
                          <Button color="primary" outline onClick={() => downloadSplash(splash.Id, splash.ImageData)} style={{ marginLeft: "0.5rem" }}>
                            Download
                          </Button>
                        )}
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Loading>
    </>
  );
};
